import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      // Session token is in httpOnly cookie; Bearer token fallback for dev environments
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('tl_token');
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const resp = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
        headers,
      });
      if (resp.ok) {
        const data = await resp.json();
        setUser({ user_id: data.user_id, email: data.email, name: data.name, role: data.role, email_verified: data.email_verified });
        setTenant(data.tenant);
        return data;
      } else {
        console.error('fetchMe failed:', resp.status, resp.statusText);
      }
    } catch (err) {
      console.error('fetchMe error:', err);
    }
    setUser(null);
    setTenant(null);
    return null;
  }, []);

  useEffect(() => {
    // On mount, check if we have an active session (via httpOnly cookie)
    fetchMe().finally(() => setLoading(false));
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const resp = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }), 
        credentials: 'include',  // Important: sends and receives cookies
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Login failed');
      // Store token as Bearer fallback (for environments where httpOnly cookies don't persist)
      if (data.token) localStorage.setItem('tl_token', data.token);
      await fetchMe();
      return data;
    } catch (e) {
      throw new Error(e.message || 'Login failed');
    }
  };

  const register = async (email, password, name) => {
    const resp = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.detail || 'Registration failed');
    return data;
  };

  const verifyEmail = async (token) => {
    const resp = await fetch(`${API_URL}/api/auth/verify-email`, {
      method: 'POST', 
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }), 
      credentials: 'include',  // Receives httpOnly cookie after verification
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.detail || 'Verification failed');
    // Session token is now in httpOnly cookie
    await fetchMe();
    return data;
  };

  const confirmDomain = async (companyName, tz, scanHour) => {
    // Session token is in httpOnly cookie - automatically sent with request
    const resp = await fetch(`${API_URL}/api/auth/confirm-domain`, {
      method: 'POST', 
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirmed: true, company_name: companyName, tz: tz, scan_hour: scanHour }),
      credentials: 'include',
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.detail || 'Domain confirmation failed');
    return data;
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, { 
        method: 'POST', 
        credentials: 'include'  // Sends httpOnly cookie to be invalidated
      });
    } catch {}
    setUser(null);
    setTenant(null);
  };

  const isAdmin = user?.role === 'admin';
  const tier = tenant?.subscription_tier || 'free';
  const hasVigilance = tier === 'vigilance' || tier === 'ecosystem';
  const hasEcosystem = tier === 'ecosystem';
  const isFreeExpired = tier === 'free' && tenant?.trial_expires_at && new Date(tenant.trial_expires_at) < new Date();

  return (
    <AuthContext.Provider value={{
      user, tenant, loading, login, register, verifyEmail, confirmDomain, logout, fetchMe,
      isAdmin, tier, hasVigilance, hasEcosystem, isFreeExpired,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
